import {
  Box,
  CommonPopupCrud,
  customUseEffect,
  FormControlLabel,
  FormLabel,
  Grid,
  MuiAutocomplete,
  Radio,
  timeoutCall,
} from "@common";
import {
  getAssignmentType,
  getCrmTeamApi,
  getProjectUser,
} from "@features";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

const validationSchema = Yup.object({
  assignmentType: Yup.object()
    .nullable()
    .required("Assignment Type is required"),
  userId: Yup.object()
    .nullable()
    .required("User is required"),
  isPrimary: Yup.boolean(),
  startDate: Yup.mixed().nullable(),
  endDate: Yup.mixed()
    .nullable()
    .test(
      "is-after-start",
      "End Date cannot be earlier than Start Date",
      function (endDate) {
        const { startDate } = this.parent;
        if (!startDate || !endDate) return true;
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        if (!start.isValid() || !end.isValid()) return true;
        return !end.isBefore(start, "day");
      }
    ),
});

const sanitizeId = (id) => {
  if (id === true || id === false || id === "true" || id === "false" || id === undefined || id === null) {
    return null;
  }
  return id;
};

const getSelectedId = (data, fallbackId) => {
  const cleanFallback = sanitizeId(fallbackId);
  if (Array.isArray(data)) {
    return sanitizeId(data[0]?.id || data[0]?._id) || cleanFallback || null;
  }
  return sanitizeId(data?.id || data?._id) || cleanFallback || null;
};

const getAssignmentTypeValue = (assignmentType) => {
  if (typeof assignmentType === "string") {
    return assignmentType;
  }
  return assignmentType?.assignmentType || assignmentType?.name || "";
};

const getUserIdValue = (userId) => {
  return userId?.id || userId?.userId || userId || null;
};

const formatDate = (date) => {
  if (date && dayjs(date).isValid()) {
    return dayjs(date).format("YYYY-MM-DD");
  }
  return null;
};

const isRequestSuccessful = (res) =>
  Boolean(
    res?.payload?.ok ||
    res?.payload?.data?.ok ||
    res?.meta?.requestStatus === "fulfilled" ||
    !res?.error
  );

const UpdatePage = ({
  rowData,
  selectedData,
  forCampaign,
  forProspect,
  forInquiry,
  forLead,
  forOpportunity,
  forAccount,
  forCustomer,
  forContact,
  forContract,
  forOrder,
  campaignId,
  prospectId,
  inquiryId,
  leadId,
  opportunityId,
  customerAccountId,
  customerId,
  contactId,
  contractId,
  orderId,
  ...props
}) => {
  const data = rowData || selectedData || {};

  const loading = useSelector((state) => state?.crmTeam?.loading);
  const assignmentTypeOptions = useSelector((state) => state?.assignmentType?.data) || [];
  const projectUsersOptions = useSelector((state) => state?.projectUsers?.data) || [];

  const dispatch = useDispatch();

  customUseEffect(() => {
    dispatch(getAssignmentType());
    dispatch(getProjectUser());
  }, []);

  const campaignSelectedData = useSelector((state) => state?.campaign?.selectedData);
  const prospectSelectedData = useSelector((state) => state?.prospect?.selectedData);
  const inquirySelectedData = useSelector((state) => state?.inquiry?.selectedData);
  const leadSelectedData = useSelector((state) => state?.lead?.selectedData);
  const opportunitySelectedData = useSelector((state) => state?.opportunity?.selectedData);
  const accountSelectedData = useSelector((state) => state?.account?.selectedData);
  const customerSelectedData = useSelector((state) => state?.customer?.selectedData);
  const contactSelectedData = useSelector((state) => state?.contact?.selectedData);
  const contractSelectedData = useSelector((state) => state?.contract?.selectedData);
  const orderSelectedData = useSelector((state) => state?.order?.selectedData);

  const propsWithFlags = {
    forCampaign,
    forProspect,
    forInquiry,
    forLead,
    forOpportunity,
    forAccount,
    forCustomer,
    forContact,
    forContract,
    forOrder,
    campaignId,
    prospectId,
    inquiryId,
    leadId,
    opportunityId,
    customerAccountId,
    customerId,
    contactId,
    contractId,
    orderId,
    ...props,
  };

  const api = useMemo(
    () => getCrmTeamApi(propsWithFlags),
    [
      forCampaign,
      forProspect,
      forInquiry,
      forLead,
      forOpportunity,
      forAccount,
      forCustomer,
      forContact,
      forContract,
      forOrder,
      campaignId,
      prospectId,
      inquiryId,
      leadId,
      opportunityId,
      customerAccountId,
      customerId,
      contactId,
      contractId,
      orderId,
      props?.entityType,
    ]
  );

  const activeEntityId = useMemo(() => {
    const propId = sanitizeId(propsWithFlags?.[api.idKey]);
    if (propId && (typeof propId === "string" || typeof propId === "number")) {
      return propId;
    }
    let resolved = null;
    switch (api.entityType) {
      case "contract":
        resolved = sanitizeId(contractId) || sanitizeId(props?.contractId) || getSelectedId(contractSelectedData, data?.contractId);
        break;
      case "customer-account":
        resolved = sanitizeId(customerAccountId) || sanitizeId(props?.customerAccountId) || getSelectedId(accountSelectedData, data?.customerAccountId);
        break;
      case "contact":
        resolved = sanitizeId(contactId) || sanitizeId(props?.contactId) || getSelectedId(contactSelectedData, data?.contactId);
        break;
      case "opportunity":
        resolved = sanitizeId(opportunityId) || sanitizeId(props?.opportunityId) || getSelectedId(opportunitySelectedData, data?.opportunityId);
        break;
      case "lead":
        resolved = sanitizeId(leadId) || sanitizeId(props?.leadId) || getSelectedId(leadSelectedData, data?.leadId);
        break;
      case "campaign":
        resolved = sanitizeId(campaignId) || sanitizeId(props?.campaignId) || getSelectedId(campaignSelectedData, data?.campaignId);
        break;
      case "inquiry":
        resolved = sanitizeId(inquiryId) || sanitizeId(props?.inquiryId) || getSelectedId(inquirySelectedData, data?.inquiryId);
        break;
      case "prospect":
        resolved = sanitizeId(prospectId) || sanitizeId(props?.prospectId) || getSelectedId(prospectSelectedData, data?.prospectId);
        break;
      case "customer":
        resolved = sanitizeId(customerId) || sanitizeId(props?.customerId) || getSelectedId(customerSelectedData, data?.customerId);
        break;
      case "order":
        resolved = sanitizeId(orderId) || sanitizeId(props?.orderId) || getSelectedId(orderSelectedData, data?.orderId);
        break;
      default:
        resolved = null;
    }
    return sanitizeId(resolved);
  }, [
    api.entityType,
    api.idKey,
    propsWithFlags,
    contractId,
    customerAccountId,
    contactId,
    opportunityId,
    leadId,
    campaignId,
    inquiryId,
    prospectId,
    customerId,
    orderId,
    props?.contractId,
    props?.customerAccountId,
    props?.contactId,
    props?.opportunityId,
    props?.leadId,
    props?.campaignId,
    props?.inquiryId,
    props?.prospectId,
    props?.customerId,
    props?.orderId,
    contractSelectedData,
    accountSelectedData,
    contactSelectedData,
    opportunitySelectedData,
    leadSelectedData,
    campaignSelectedData,
    inquirySelectedData,
    prospectSelectedData,
    customerSelectedData,
    orderSelectedData,
    data,
  ]);

  const resolvedAssignmentType = useMemo(() => {
    const at = data?.assignmentType;
    if (!at) return null;
    if (typeof at === "object") return at;
    return (
      assignmentTypeOptions.find(
        (item) =>
          item?.assignmentType === at ||
          item?.name === at ||
          item?.id === at
      ) || { assignmentType: at }
    );
  }, [data?.assignmentType, assignmentTypeOptions]);

  const resolvedUser = useMemo(() => {
    const u = data?.user || data?.userId;
    if (!u) return null;
    if (typeof data?.user === "object" && data?.user) return data.user;
    const uid = typeof u === "object" ? u?.id || u?.userId : u;
    return (
      projectUsersOptions.find(
        (item) =>
          item?.id === uid ||
          item?.userId === uid ||
          item?.email === uid
      ) || (typeof u === "object" ? u : { userName: String(uid), id: uid })
    );
  }, [data?.user, data?.userId, projectUsersOptions]);

  const initialStartDate = useMemo(() => {
    const raw = data?.startDate || data?.start_date;
    return raw && dayjs(raw).isValid() ? dayjs(raw) : null;
  }, [data?.startDate, data?.start_date]);

  const initialEndDate = useMemo(() => {
    const raw = data?.endDate || data?.end_date;
    return raw && dayjs(raw).isValid() ? dayjs(raw) : null;
  }, [data?.endDate, data?.end_date]);

  const formik = useFormik({
    initialValues: {
      assignmentType: resolvedAssignmentType,
      userId: resolvedUser,
      isPrimary: Boolean(data?.isPrimary),
      startDate: initialStartDate,
      endDate: initialEndDate,
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const payload = {
        id: data?.id || rowData?.id || selectedData?.id,
        assignmentType: getAssignmentTypeValue(values?.assignmentType),
        userId: getUserIdValue(values?.userId),
        isPrimary: Boolean(values?.isPrimary),
        startDate: formatDate(values?.startDate),
        endDate: formatDate(values?.endDate),
        ...(sanitizeId(activeEntityId) ? { [api.idKey]: sanitizeId(activeEntityId) } : {}),
      };

      const res = await dispatch(api.update(payload));
      if (isRequestSuccessful(res)) {
        props?.getFunction?.();
        timeoutCall(() => {
          formik.resetForm();
          props?.close?.();
        }, 500);
        return true;
      }
      return false;
    },
  });

  const handleSubmit = useCallback(async () => {
    const result = await formik?.submitForm();
    return result;
  }, [formik]);

  return (
    <CommonPopupCrud
      open={props.showUpdate ?? true}
      heading="Update Team Member"
      close={props.close}
      mediumScreen={true}
      handleSubmit={handleSubmit}
      hideSaveAndNext={true}
      loading={loading}
    >
      <Box
        component="form"
        onSubmit={formik.handleSubmit}
        noValidate
        sx={{ mt: 1 }}
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <MuiAutocomplete
              name="assignmentType"
              label="Assignment Type"
              required={true}
              fullWidth={true}
              margin="normal"
              options={assignmentTypeOptions}
              getOptionLabel={(option) => option?.assignmentType || option?.name || ""}
              value={formik.values.assignmentType}
              onChange={(e, val) => formik.setFieldValue("assignmentType", val)}
              error={
                formik.touched.assignmentType &&
                Boolean(formik.errors.assignmentType)
              }
              helperText={
                formik.touched.assignmentType &&
                formik.errors.assignmentType
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <MuiAutocomplete
              name="userId"
              label="User"
              required={true}
              fullWidth={true}
              margin="normal"
              options={projectUsersOptions}
              getOptionLabel={(option) =>
                option?.userName || option?.email || option?.firstName || ""
              }
              value={formik.values.userId}
              onChange={(e, val) => formik.setFieldValue("userId", val)}
              error={
                formik.touched.userId && Boolean(formik.errors.userId)
              }
              helperText={formik.touched.userId && formik.errors.userId}
            />
          </Grid>
        </Grid>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DatePicker
                label="Start Date"
                value={formik.values.startDate}
                onChange={(newValue) =>
                  formik.setFieldValue("startDate", newValue)
                }
                format="DD-MM-YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: "normal",
                    error:
                      formik.touched.startDate &&
                      Boolean(formik.errors.startDate),
                    helperText:
                      formik.touched.startDate &&
                      formik.errors.startDate,
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DatePicker
                label="End Date"
                disabled={!formik.values.startDate}
                value={formik.values.endDate}
                onChange={(newValue) =>
                  formik.setFieldValue("endDate", newValue)
                }
                format="DD-MM-YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: "normal",
                    error:
                      formik.touched.endDate &&
                      Boolean(formik.errors.endDate),
                    helperText:
                      formik.touched.endDate && formik.errors.endDate,
                  },
                }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
        <Box sx={{ mt: 2 }}>
          <FormLabel component="legend">Primary</FormLabel>
          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControlLabel
              control={
                <Radio
                  checked={formik.values.isPrimary === true}
                  onChange={() => formik.setFieldValue("isPrimary", true)}
                  value={true}
                  name="isPrimary"
                />
              }
              label="True"
            />
            <FormControlLabel
              control={
                <Radio
                  checked={formik.values.isPrimary === false}
                  onChange={() => formik.setFieldValue("isPrimary", false)}
                  value={false}
                  name="isPrimary"
                />
              }
              label="False"
            />
          </Box>
        </Box>
      </Box>
    </CommonPopupCrud>
  );
};

export default UpdatePage;
